import {
  ATTACHMENT_ERROR_CODES,
  ATTACHMENT_KINDS,
  ATTACHMENT_KIND_LABELS,
  type AttachmentKind,
  type AttachmentSlot,
  type TicketDetail,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useId, useState } from 'react';
import { useSession } from '../../api/auth';
import { ApiError } from '../../api/client';
import {
  downloadActionLog,
  downloadScreenshot,
  useAttachmentAccesses,
  useOpenAttachment,
  useTicketAttachments,
} from '../../api/ticket-attachments';
import { formatDateTime } from '../../components/ticket';
import { ActionLogViewer } from './ActionLogViewer';
import {
  ATTACHMENT_ROW_LABELS,
  NO_SESSION_TEXT,
  OPEN_LABELS,
  VIA_MCP_NOTE,
  accessOriginText,
  describeAttachmentSlot,
} from './attachment-text';
import { FactList } from './DetailSection';
import { ScreenshotViewer } from './ScreenshotViewer';
import { WithViaMcpAbbr } from './ViaMcp';
import { useObjectUrl } from './use-object-url';

/** Avviso accanto ai comandi di apertura: ogni apertura è un accesso registrato (easycare DEC-101). */
export const TRACKED_ACCESS_NOTICE =
  'Schermata e log si scaricano solo quando li apri: ogni apertura è registrata a tuo nome, con data e ora.';

type Ticket = Pick<TicketDetail, 'code' | 'channel' | 'context'>;

/**
 * Presenza o assenza di schermata e log nel contesto (story #8, DEC-010): sul pre-accesso
 * l'assenza è dichiarata per costruzione, senza chiamare l'API; altrimenti lo stato di ciascuno con
 * la causa nota e, se custodito, il comando per aprirlo. Lo stato si legge al caricamento (sono
 * metadati); il contenuto mai, solo su richiesta.
 */
export function ContextAttachments({ ticket }: { ticket: Ticket }) {
  if (ticket.channel === 'pre-accesso') {
    return (
      <FactList
        facts={ATTACHMENT_KINDS.map((kind) => ({ label: ATTACHMENT_ROW_LABELS[kind], value: NO_SESSION_TEXT[kind] }))}
      />
    );
  }
  return <ConsumerAttachments code={ticket.code} screenLabel={ticket.context.screenLabel} />;
}

function ConsumerAttachments({ code, screenLabel }: { code: string; screenLabel: string | null }) {
  const { data, isPending, isError, error, refetch, isFetching } = useTicketAttachments(code);
  const { data: session } = useSession();
  const isAdmin = session?.member.role === 'amministratore';

  if (isPending) {
    return (
      <Typography variant="body2" color="text.secondary" role="status">
        Verifica di schermata e log…
      </Typography>
    );
  }
  if (isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void refetch()} disabled={isFetching}>
            Riprova
          </Button>
        }
      >
        Stato di schermata e log non disponibile: {error.message}
      </Alert>
    );
  }

  const anyStored = ATTACHMENT_KINDS.some((kind) => data.allegati[kind].stato === 'custodito');
  return (
    <Stack spacing={2}>
      <FactList
        facts={ATTACHMENT_KINDS.map((kind) => ({
          label: ATTACHMENT_ROW_LABELS[kind],
          value: (
            <AttachmentRow
              code={code}
              kind={kind}
              slot={data.allegati[kind]}
              uploadDeadline={data.caricamentoEntro}
              screenLabel={screenLabel}
              isAdmin={isAdmin}
            />
          ),
        }))}
      />
      {anyStored && (
        <Typography variant="caption" color="text.secondary" role="note">
          {TRACKED_ACCESS_NOTICE}
        </Typography>
      )}
    </Stack>
  );
}

interface AttachmentRowProps {
  code: string;
  kind: AttachmentKind;
  slot: AttachmentSlot;
  uploadDeadline: string | null;
  screenLabel: string | null;
  isAdmin: boolean;
}

function AttachmentRow({ code, kind, slot, uploadDeadline, screenLabel, isAdmin }: AttachmentRowProps) {
  const attachment = slot.allegato;
  return (
    <Stack spacing={1.5} alignItems="flex-start">
      <span>{describeAttachmentSlot(kind, slot, uploadDeadline)}</span>
      {slot.stato === 'rifiutato' && slot.rifiuti.length > 1 && (
        <Box component="ul" aria-label={`Rifiuti: ${ATTACHMENT_KIND_LABELS[kind]}`} sx={{ m: 0, pl: 5 }}>
          {slot.rifiuti.map((rejection) => (
            <li key={`${rejection.il}:${rejection.codice}`}>
              {formatDateTime(rejection.il)} · {rejection.motivo}
            </li>
          ))}
        </Box>
      )}
      {/* Montati anche dopo la retention: l'avviso di un 410 resta accanto allo stato riallineato. */}
      {attachment &&
        (kind === 'schermata' ? (
          <OpenScreenshot
            code={code}
            attachmentId={attachment.id}
            available={slot.stato === 'custodito'}
            screenLabel={screenLabel}
          />
        ) : (
          <OpenLog code={code} attachmentId={attachment.id} available={slot.stato === 'custodito'} />
        ))}
      {isAdmin && attachment && <AttachmentAccesses code={code} kind={kind} attachmentId={attachment.id} />}
    </Stack>
  );
}

/**
 * Comando di apertura: durante il download non si disattiva (`aria-disabled`), così il focus resta
 * lì e il visore, chiuso, ce lo riporta; un secondo clic nel frattempo non scarica di nuovo.
 */
function OpenButton({ kind, pending, onOpen }: { kind: AttachmentKind; pending: boolean; onOpen: () => void }) {
  return (
    <Button size="small" variant="outlined" aria-disabled={pending} onClick={() => !pending && onOpen()}>
      {pending ? 'Apertura…' : OPEN_LABELS[kind]}
    </Button>
  );
}

/** «Mostra la schermata»: download su richiesta, URL `blob:` revocato e mutation azzerata alla chiusura. */
function OpenScreenshot({
  code,
  attachmentId,
  available,
  screenLabel,
}: {
  code: string;
  attachmentId: string;
  /** Contenuto custodito: senza, niente comando (resta l'eventuale esito dell'ultima apertura). */
  available: boolean;
  screenLabel: string | null;
}) {
  const [url, show] = useObjectUrl();
  const opening = useOpenAttachment(code, attachmentId, downloadScreenshot);
  const close = () => {
    show(null);
    opening.reset();
  };
  return (
    <>
      {available && (
        <OpenButton
          kind="schermata"
          pending={opening.isPending}
          onOpen={() => opening.mutate(undefined, { onSuccess: show })}
        />
      )}
      {opening.isError && <OpenError kind="schermata" error={opening.error} />}
      {url && <ScreenshotViewer src={url} code={code} screenLabel={screenLabel} onClose={close} />}
    </>
  );
}

/** «Mostra il log»: download e validazione su richiesta; alla chiusura la mutation si azzera e il log non resta in memoria. */
function OpenLog({ code, attachmentId, available }: { code: string; attachmentId: string; available: boolean }) {
  const opening = useOpenAttachment(code, attachmentId, downloadActionLog);
  return (
    <>
      {available && <OpenButton kind="log" pending={opening.isPending} onOpen={() => opening.mutate()} />}
      {opening.isError && <OpenError kind="log" error={opening.error} />}
      {opening.data && <ActionLogViewer log={opening.data} code={code} onClose={() => opening.reset()} />}
    </>
  );
}

/** Chi ha aperto il contenuto (solo amministratori): l'elenco si legge solo quando lo si apre. */
function AttachmentAccesses({
  code,
  kind,
  attachmentId,
}: {
  code: string;
  kind: AttachmentKind;
  attachmentId: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <>
      <Button
        size="small"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? 'Nascondi gli accessi' : 'Mostra gli accessi'}
      </Button>
      {open && <AttachmentAccessList id={id} code={code} kind={kind} attachmentId={attachmentId} />}
    </>
  );
}

/** Esito di un'apertura non riuscita: il contenuto eliminato nel frattempo è un avviso, non un guasto. */
function OpenError({ kind, error }: { kind: AttachmentKind; error: Error }) {
  const label = ATTACHMENT_KIND_LABELS[kind];
  if (error instanceof ApiError && error.code === ATTACHMENT_ERROR_CODES.deletedForRetention) {
    const at = typeof error.details?.eliminatoIl === 'string' ? ` il ${formatDateTime(error.details.eliminatoIl)}` : '';
    return (
      <Alert severity="warning" role="alert">
        {label}: contenuto eliminato per retention{at}. Restano solo i metadati.
      </Alert>
    );
  }
  if (error instanceof ApiError && error.code === ATTACHMENT_ERROR_CODES.storageUnavailable) {
    return (
      <Alert severity="error" role="alert">
        {label} non disponibile: l'archivio degli allegati non risponde, riprova fra poco.
      </Alert>
    );
  }
  return (
    <Alert severity="error" role="alert">
      Apertura non riuscita: {error.message}
    </Alert>
  );
}

/**
 * Chi ha aperto il contenuto e quando, dal più recente (solo amministratori); una lettura da Claude o
 * Cursor porta «via MCP» con il nome del client (#155).
 */
function AttachmentAccessList({
  id,
  code,
  kind,
  attachmentId,
}: {
  id: string;
  code: string;
  kind: AttachmentKind;
  attachmentId: string;
}) {
  const { data, isPending, isError, error } = useAttachmentAccesses(code, attachmentId);
  const label = `Accessi: ${ATTACHMENT_KIND_LABELS[kind]}`;
  if (isPending) {
    return (
      <Typography id={id} variant="body2" color="text.secondary" role="status">
        Lettura degli accessi…
      </Typography>
    );
  }
  if (isError) {
    return (
      <Alert id={id} severity="error">
        Accessi non disponibili: {error.message}
      </Alert>
    );
  }
  if (data.totale === 0) {
    return (
      <Typography id={id} variant="body2" color="text.secondary">
        Nessun accesso registrato.
      </Typography>
    );
  }
  return (
    <Box id={id}>
      <Box component="ul" aria-label={label} sx={{ m: 0, pl: 5 }}>
        {data.accessi.map((access, index) => {
          const origin = accessOriginText(access);
          return (
            // Due letture parallele dello stesso client possono avere lo stesso istante: l'indice le distingue.
            <li key={`${access.il}:${access.membro.id}:${index}`}>
              {access.membro.nome}
              {access.membro.rimosso ? ' (non più nel team)' : ''}
              <WithViaMcpAbbr>{origin ? ` · ${origin}` : ''}</WithViaMcpAbbr> · {formatDateTime(access.il)}
            </li>
          );
        })}
      </Box>
      {data.accessi.some((access) => access.viaMcp) && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          {VIA_MCP_NOTE}
        </Typography>
      )}
      {data.totale > data.accessi.length && (
        <Typography variant="caption" color="text.secondary">
          Mostrati gli ultimi {data.accessi.length} di {data.totale} accessi.
        </Typography>
      )}
    </Box>
  );
}
